/**
 * Per-deployment segments of an OPC vendor node path.
 *
 * Kone KICs expose identical leaf node names ("LiftMode", "DoorMode.Side1") at
 * every building; only the path above the leaf differs. The adapter therefore
 * holds only the leaves and stays valid for a controller type, while these
 * segments are entered per bank and per lift.
 *
 * Each name is the level of the OPC tree it populates, deliberately: all four
 * levels are independent, so a name that does not state its target level gets
 * wired to the wrong one.
 */
export const OPC_VENDOR_PATH_FIELDS = [
  "vendor_location_path",
  "vendor_site_path",
  "vendor_group_path",
] as const;

export type OpcVendorPathField = (typeof OPC_VENDOR_PATH_FIELDS)[number];

/** Operator-facing labels, phrased by what the installer reads off the server. */
export const OPC_VENDOR_PATH_LABELS: Record<OpcVendorPathField, string> = {
  vendor_location_path: "OPC Location Path",
  vendor_site_path: "OPC Site Path",
  vendor_group_path: "OPC Group Path",
};

/**
 * camelCase spelling of each field.
 *
 * A bank reaches the standalone form over either the FlatBuffer path
 * (camelCase, from the Object API) or the JSON API (snake_case), so both
 * spellings have to be read and cleared. Written out rather than derived by a
 * string transform: an explicit map is greppable, type-checked against the
 * field union, and cannot silently produce a key nothing reads.
 */
export const OPC_VENDOR_PATH_CAMEL: Record<OpcVendorPathField, string> = {
  vendor_location_path: "vendorLocationPath",
  vendor_site_path: "vendorSitePath",
  vendor_group_path: "vendorGroupPath",
};

/** Per-lift segments; the lift editor renders these next to Car Number. */
export const OPC_VENDOR_LIFT_FIELDS = ["vendor_lift_path", "vendor_group_name"] as const;

export type OpcVendorLiftField = (typeof OPC_VENDOR_LIFT_FIELDS)[number];

export const OPC_VENDOR_LIFT_LABELS: Record<OpcVendorLiftField, string> = {
  vendor_lift_path: "OPC Lift Path",
  vendor_group_name: "OPC Group Name",
};

/**
 * Protocols whose banks carry vendor path segments.
 *
 * Both OPC dialects address points by a dotted node path; no other protocol in
 * the system does, so the fields stay hidden everywhere else.
 */
export const OPC_VENDOR_LIFT_CAMEL: Record<OpcVendorLiftField, string> = {
  vendor_lift_path: "vendorLiftPath",
  vendor_group_name: "vendorGroupName",
};

export const OPC_PATH_PROTOCOLS = new Set(["OPC", "OPCUA"]);
